<script lang="ts" setup>
import { ref, watch } from 'vue'
import {
  validLineActivationProviderSettingFormSchema,
  type LineActivationProviderSettingFormErrorManager,
  type LineActivationProviderSettingFormErrorPath,
  type LineActivationProviderSettingFormErrors,
  type LineActivationProviderSettingFormType,
} from './formSchema'
import LineActivationProviderSettingFormError from './LineActivationProviderSettingFormError.vue'
import DolphinTextInput from '../../atoms/DolphinTextInput.vue'
import FlatButton from '../../atoms/FlatButton.vue'
import LoadingIndicator from '../../atoms/LoadingIndicator.vue'
import * as v from 'valibot'
import type { UpdateLineActivationProviderSettingInput } from '@/types/schema.generated'

const props = defineProps<{
  initialForm: LineActivationProviderSettingFormType
  isSubmitting: boolean
}>()

const emit = defineEmits<{
  (event: 'submit', input: UpdateLineActivationProviderSettingInput): void
}>()

const showValidate = ref(false)
const form = ref<LineActivationProviderSettingFormType>({
  ...props.initialForm,
})
const formErrors = ref<LineActivationProviderSettingFormErrors>()
const formErrorManager: LineActivationProviderSettingFormErrorManager = {
  get: (
    path: LineActivationProviderSettingFormErrorPath
  ): string | undefined => {
    const errors = formErrors.value
    if (!errors) {
      return undefined
    }

    const messages = errors.nested?.[path]
    if (!messages) {
      return undefined
    }

    return messages[0]
  },
}

watch(
  () => props.initialForm,
  newInitialForm => {
    if (newInitialForm) {
      form.value = { ...newInitialForm }
    }
  },
  { deep: true }
)

watch(form.value, () => {
  if (showValidate.value) {
    const validationResult = validate()
    if (!validationResult.success) {
      formErrors.value = validationResult.errors
    }
  }
})

const handleSubmit = () => {
  showValidate.value = true

  const validationResult = validate()

  if (!validationResult.success) {
    formErrors.value = validationResult.errors
    return
  }

  emit('submit', validationResult.output)
}

type ValidateResponse =
  | {
      success: true
      output: v.InferOutput<typeof validLineActivationProviderSettingFormSchema>
    }
  | {
      success: false
      errors: LineActivationProviderSettingFormErrors
    }

const validate = (): ValidateResponse => {
  const result = v.safeParse(validLineActivationProviderSettingFormSchema, {
    ...form.value,
  })

  formErrors.value = undefined

  if (result.success) {
    return {
      success: true,
      output: result.output,
    }
  } else {
    return {
      success: false,
      errors: v.flatten(result.issues),
    }
  }
}
</script>

<template>
  <div :class="$style.form">
    <h1>LINE連携設定</h1>
    <section>
      <p :class="$style.sectionTitle">Messaging APIチャネルアクセストークン</p>
      <div :class="$style.sectionContent">
        <DolphinTextInput
          v-model="form.messageApiAccessToken"
          :class="$style.textInput"
        />
        <LineActivationProviderSettingFormError
          v-if="formErrorManager.get('messageApiAccessToken')"
        >
          {{ formErrorManager.get('messageApiAccessToken') }}
        </LineActivationProviderSettingFormError>
      </div>
    </section>
    <section>
      <p :class="$style.sectionTitle">Messaging APIチャネルシークレット</p>
      <div :class="$style.sectionContent">
        <DolphinTextInput
          v-model="form.messageApiChannelSecret"
          :class="$style.textInput"
        />
        <LineActivationProviderSettingFormError
          v-if="formErrorManager.get('messageApiChannelSecret')"
        >
          {{ formErrorManager.get('messageApiChannelSecret') }}
        </LineActivationProviderSettingFormError>
      </div>
    </section>
    <section v-if="form.lineChannelId !== undefined">
      <p :class="$style.sectionTitle">LINEチャネルID</p>
      <div :class="$style.sectionContent">
        <DolphinTextInput
          v-model="form.lineChannelId"
          :class="$style.textInput"
        />
        <LineActivationProviderSettingFormError
          v-if="formErrorManager.get('lineChannelId')"
        >
          {{ formErrorManager.get('lineChannelId') }}
        </LineActivationProviderSettingFormError>
      </div>
    </section>
    <section v-if="form.liffId !== undefined">
      <p :class="$style.sectionTitle">LIFF ID</p>
      <div :class="$style.sectionContent">
        <DolphinTextInput v-model="form.liffId" :class="$style.textInput" />
        <LineActivationProviderSettingFormError
          v-if="formErrorManager.get('liffId')"
        >
          {{ formErrorManager.get('liffId') }}
        </LineActivationProviderSettingFormError>
      </div>
    </section>
    <section v-if="form.lineFollowUrl !== undefined">
      <p :class="$style.sectionTitle">LINE友だち追加URL</p>
      <div :class="$style.sectionContent">
        <DolphinTextInput
          v-model="form.lineFollowUrl"
          :class="$style.textInput"
        />
        <LineActivationProviderSettingFormError
          v-if="formErrorManager.get('lineFollowUrl')"
        >
          {{ formErrorManager.get('lineFollowUrl') }}
        </LineActivationProviderSettingFormError>
      </div>
    </section>
    <div :class="$style.submitButtonWrapper">
      <FlatButton
        :disabled="props.isSubmitting"
        @click="handleSubmit"
        variant="primary"
        size="large"
      >
        <LoadingIndicator v-if="props.isSubmitting" />
        保存
      </FlatButton>
    </div>
  </div>
</template>

<style lang="scss" module>
.form {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.submitButtonWrapper {
  display: flex;
  justify-content: center;
}

.sectionTitle {
  font-size: 18px;
  font-weight: bold;
}

.sectionContent {
  margin-top: 10px;
}

.textInput {
  width: 100%;
  margin-top: 4px;
}
</style>
